import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Input } from '@cotera/client/v0/components/ui/input';
import { Text } from '@cotera/client/app/components/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@cotera/client/v0/components/ui/select';
import { cn } from '@cotera/client/v0/lib/utils';

type JsonSchemaProperty = {
  type?: string | string[];
  title?: string;
  description?: string;
  enum?: readonly string[];
  properties?: Record<string, JsonSchemaProperty>;
  required?: readonly string[];
};

type JsonSchemaRoot = {
  type?: string;
  properties?: Record<string, JsonSchemaProperty>;
  required?: readonly string[];
};

function resolvePrimaryType(schema: JsonSchemaProperty): string | undefined {
  if (typeof schema.type === 'string') {
    return schema.type;
  }
  if (Array.isArray(schema.type)) {
    return schema.type.find((entry) => entry !== 'null');
  }
  return undefined;
}

function toRecord(value: unknown): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return {};
  }
  return value as Record<string, unknown>;
}

export const CatalogSchemaForm: React.FC<{
  schema: Record<string, unknown> | undefined;
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  className?: string;
}> = ({ schema, value, onChange, className }) => {
  const parsed = schema as JsonSchemaRoot | undefined;
  const properties = parsed?.properties ?? {};
  const requiredKeys = new Set(parsed?.required ?? []);
  const methods = useForm<{ values: Record<string, unknown> }>({
    defaultValues: { values: value },
  });

  useEffect(() => {
    methods.reset({ values: value });
  }, [methods, value]);

  useEffect(() => {
    const subscription = methods.watch((formValues) => {
      onChange(toRecord(formValues.values));
    });
    return () => subscription.unsubscribe();
  }, [methods, onChange]);

  const entries = Object.entries(properties);
  if (entries.length === 0) {
    return null;
  }

  return (
    <div className={cn('space-y-3', className)}>
      {entries.map(([key, propertySchema]) => {
        const fieldType = resolvePrimaryType(propertySchema);
        const label = propertySchema.title ?? key;
        const fieldPath = `values.${key}` as const;
        const isRequired = requiredKeys.has(key);
        return (
          <div key={key} className="space-y-1.5">
            <Text.Caption className="text-muted-foreground block">
              {label}
              {isRequired ? ' *' : ''}
            </Text.Caption>
            {propertySchema.description !== undefined &&
            propertySchema.description !== '' ? (
              <Text.Caption className="text-muted-foreground block text-xs">
                {propertySchema.description}
              </Text.Caption>
            ) : null}
            {propertySchema.enum !== undefined &&
            propertySchema.enum.length > 0 ? (
              <Select
                value={String(methods.watch(fieldPath) ?? '')}
                onValueChange={(next) => methods.setValue(fieldPath, next)}
              >
                <SelectTrigger className="h-9 w-full">
                  <SelectValue placeholder={`Select ${label}`} />
                </SelectTrigger>
                <SelectContent>
                  {propertySchema.enum.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : fieldType === 'boolean' ? (
              <Select
                value={String(methods.watch(fieldPath) ?? 'false')}
                onValueChange={(next) =>
                  methods.setValue(fieldPath, next === 'true')
                }
              >
                <SelectTrigger className="h-9 w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">True</SelectItem>
                  <SelectItem value="false">False</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <Input
                type={
                  fieldType === 'number' || fieldType === 'integer'
                    ? 'number'
                    : 'text'
                }
                {...methods.register(fieldPath, {
                  setValueAs: (raw: string) => {
                    if (fieldType === 'number' || fieldType === 'integer') {
                      const parsedNumber = Number(raw);
                      return Number.isFinite(parsedNumber) ? parsedNumber : raw;
                    }
                    return raw;
                  },
                })}
                placeholder={label}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};
